import { useEffect, useRef, useState } from "preact/hooks";
import { ApiError, api, type ForwardLogEntry, type ForwardRule, type RuleFields, type RulePreview } from "./api";
import { listDate, relativeTime, sender } from "./format";
import { Icon } from "./icons";
import { invalidAddresses, parseAddresses } from "./outgoing";

const PREVIEW_DELAY_MS = 400;

/** A rule's name, else what it looks for. */
export function ruleTitle(rule: Pick<ForwardRule, "name" | "from" | "subject">): string {
  return rule.name || [rule.from, rule.subject && `“${rule.subject}”`].filter(Boolean).join(" · ") || "Rule";
}

function conditions(rule: Pick<ForwardRule, "from" | "subject">): string {
  return [rule.from && `Sender has ${rule.from}`, rule.subject && `subject has “${rule.subject}”`].filter(Boolean).join(" and ");
}

function sentence(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) + (/[.!?]$/.test(text) ? "" : ".") : text;
}

interface ListProps {
  /** null while loading. */
  rules: ForwardRule[] | null;
  selected: string | undefined;
  onOpen: (id: string) => void;
  onMenu: () => void;
}

/** The forwarding rules with what each looks for, where it forwards to and how much it forwarded. */
export function RuleList({ rules, selected, onOpen, onMenu }: ListProps) {
  return (
    <>
      <div class="toolbar">
        <button class="icon-button menu" title="Folders" onClick={onMenu}>
          <Icon name="menu" />
        </button>
        <h2 class="title">Forwarding rules</h2>
        <span class="spacer" />
        <button class="icon-button" title="New rule" onClick={() => onOpen("new")}>
          <Icon name="plus" />
        </button>
      </div>
      <div class="list">
        {rules === null ? (
          <div class="sentinel">
            <span class="spinner" aria-label="Loading" />
          </div>
        ) : rules.length === 0 ? (
          <div class="empty">
            <p>No forwarding rules yet.</p>
            <p>A rule forwards new mail from a sender, or with a word in its subject, to another address with its attachments.</p>
            <button class="primary" onClick={() => onOpen("new")}>
              New rule
            </button>
          </div>
        ) : (
          <ul role="list">
            {rules.map((r) => (
              <li key={r.id}>
                <button class={`row ${selected === String(r.id) ? "selected" : ""} ${r.enabled ? "" : "off"}`} onClick={() => onOpen(String(r.id))}>
                  <span class="row-top">
                    <span class="row-from">{ruleTitle(r)}</span>
                    <span class="row-date">{listDate(r.last_forwarded_at)}</span>
                  </span>
                  <span class="row-subject wrap">{conditions(r)}</span>
                  <span class="row-snippet">→ {r.to.join(", ")}</span>
                  <span class="row-meta">
                    {!r.enabled && <span class="tag">Off</span>}
                    <span class="tag">{r.forwarded.toLocaleString()} forwarded</span>
                    {r.pending > 0 && <span class="tag current">{r.pending} waiting</span>}
                    {r.failed > 0 && <span class="tag warn">{r.failed} failed</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

interface PaneProps {
  /** The rule's id, or "new". */
  id: string;
  rules: ForwardRule[] | null;
  onBack: () => void;
  onSaved: (rule: ForwardRule, created: boolean) => void;
  onDeleted: () => void;
  /** Open a message (from the preview or the log); folder: where it is, if known. */
  onOpenMessage: (id: string, folder: string | null) => void;
  onError: (error: unknown) => void;
}

/** The rule being edited, once the rules are loaded. */
export function RulePane({ id, rules, ...props }: PaneProps) {
  if (id === "new") return <RuleEditor key="new" rule={null} {...props} />;
  const rule = rules?.find((r) => String(r.id) === id);
  if (rule) return <RuleEditor key={id} rule={rule} {...props} />;
  return (
    <div class="reader">
      <EditorToolbar title="Forwarding rule" onBack={props.onBack} />
      {rules === null ? (
        <div class="sentinel">
          <span class="spinner" aria-label="Loading" />
        </div>
      ) : (
        <p class="empty">This rule does not exist (any more).</p>
      )}
    </div>
  );
}

function EditorToolbar({ title, onBack, children }: { title: string; onBack: () => void; children?: preact.ComponentChildren }) {
  return (
    <div class="toolbar reader-toolbar">
      <button class="icon-button back" title="Back" onClick={onBack}>
        <Icon name="back" />
      </button>
      <h2 class="title">{title}</h2>
      <span class="spacer" />
      {children}
    </div>
  );
}

interface Fields {
  name: string;
  from: string;
  subject: string;
  to: string;
  enabled: boolean;
}

const fieldsOf = (rule: ForwardRule | null): Fields => ({
  name: rule?.name ?? "",
  from: rule?.from ?? "",
  subject: rule?.subject ?? "",
  to: rule?.to.join(", ") ?? "",
  enabled: rule?.enabled ?? true,
});

const sameFields = (a: Fields, b: Fields) => (Object.keys(a) as (keyof Fields)[]).every((k) => a[k] === b[k]);

/** A new rule or one to change. While its conditions are typed it shows the archived mail they match (only shown:
 * mail that is here already is never forwarded); a saved rule also shows what it forwarded. */
function RuleEditor({ rule, onBack, onSaved, onDeleted, onOpenMessage, onError }: Omit<PaneProps, "id" | "rules"> & { rule: ForwardRule | null }) {
  const [fields, setFields] = useState<Fields>(() => fieldsOf(rule));
  const [saved, setSaved] = useState<Fields>(() => fieldsOf(rule));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [preview, setPreview] = useState<RulePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [log, setLog] = useState<ForwardLogEntry[] | null>(null);
  const first = useRef<HTMLInputElement>(null);

  const update = (change: Partial<Fields>) => {
    setFields((f) => ({ ...f, ...change }));
    setProblem(null);
  };

  // The archived mail the conditions match, a moment after typing stops.
  useEffect(() => {
    const from = fields.from.trim();
    const subject = fields.subject.trim();
    if (!from && !subject) {
      setPreview(null);
      setPreviewing(false);
      return;
    }
    let live = true;
    setPreviewing(true);
    const t = setTimeout(() => {
      api
        .previewRule(from, subject)
        .then(
          (p) => live && setPreview(p),
          (e) => live && onError(e),
        )
        .finally(() => live && setPreviewing(false));
    }, PREVIEW_DELAY_MS);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [fields.from, fields.subject]);

  const loadLog = () => {
    if (rule) api.forwardLog(rule.id).then((r) => setLog(r.forwards), onError);
  };
  useEffect(loadLog, []);
  useEffect(() => {
    if (!rule) first.current?.focus({ preventScroll: true });
  }, []);

  const save = async () => {
    const to = parseAddresses(fields.to);
    if (!fields.from.trim() && !fields.subject.trim()) return setProblem("Give a sender, a subject text or both.");
    if (to.length === 0) return setProblem("Add the address to forward to.");
    const bad = invalidAddresses(to);
    if (bad.length > 0) return setProblem(`Not an e-mail address: ${bad.join(", ")}`);
    const body: RuleFields = { name: fields.name, from: fields.from, subject: fields.subject, to, enabled: fields.enabled };
    setBusy(true);
    try {
      const stored = rule ? await api.updateRule(rule.id, body) : await api.createRule(body);
      setFields(fieldsOf(stored));
      setSaved(fieldsOf(stored));
      onSaved(stored, !rule);
      loadLog();
    } catch (e) {
      if (e instanceof ApiError && e.status < 500) setProblem(sentence(e.message));
      else onError(e);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!rule || !confirm(`Delete the rule “${ruleTitle(rule)}”?`)) return;
    setBusy(true);
    try {
      await api.deleteRule(rule.id);
      onDeleted();
    } catch (e) {
      onError(e);
      setBusy(false);
    }
  };

  const addressInput = { type: "text", inputMode: "email", autocomplete: "off", autocapitalize: "off", autocorrect: "off", spellcheck: false } as const;
  const more = preview ? preview.total - preview.messages.length : 0;

  return (
    <div class="reader rule-editor">
      <EditorToolbar title={rule ? "Forwarding rule" : "New forwarding rule"} onBack={onBack}>
        {rule && (
          <button class="icon-button" title="Delete rule" disabled={busy} onClick={remove}>
            <Icon name="trash" />
          </button>
        )}
        <button class="primary send-button" disabled={busy || (rule !== null && sameFields(fields, saved))} onClick={save}>
          {busy && <span class="spinner on-accent" aria-hidden="true" />}
          Save
        </button>
      </EditorToolbar>

      {problem && (
        <div class="banner problem" role="alert">
          <span>{problem}</span>
          <button class="icon-button" title="Dismiss" onClick={() => setProblem(null)}>
            <Icon name="close" size={16} />
          </button>
        </div>
      )}

      <div class="rule-fields">
        <label class="compose-field">
          <span>Sender</span>
          <input ref={first} {...addressInput} placeholder="contains, e.g. hej@enkelenergi.dk" value={fields.from} onInput={(e) => update({ from: (e.target as HTMLInputElement).value })} />
        </label>
        <label class="compose-field">
          <span>Subject</span>
          <input type="text" placeholder="contains, e.g. regning" value={fields.subject} onInput={(e) => update({ subject: (e.target as HTMLInputElement).value })} />
        </label>
        <label class="compose-field">
          <span>Forward to</span>
          <input {...addressInput} placeholder="addresses, separated by commas" value={fields.to} onInput={(e) => update({ to: (e.target as HTMLInputElement).value })} />
        </label>
        <label class="compose-field">
          <span>Name</span>
          <input type="text" placeholder="optional" value={fields.name} onInput={(e) => update({ name: (e.target as HTMLInputElement).value })} />
        </label>
        <label class="rule-toggle">
          <input type="checkbox" checked={fields.enabled} onChange={(e) => update({ enabled: (e.target as HTMLInputElement).checked })} />
          <span>Forward new mail with this rule</span>
        </label>
      </div>
      <p class="hint">
        With both a sender and a subject text, both must match; capitals do not matter. New mail is forwarded within about a minute of
        arriving, with its attachments. Mail already here, mail in Sent, Drafts, Trash or spam, and mail from your own address are never
        forwarded.
      </p>

      {rule && (
        <>
          <div class="section-head">
            <span>Forwarded by this rule</span>
            <span>{rule.forwarded.toLocaleString()}</span>
          </div>
          {log === null ? (
            <div class="sentinel">
              <span class="spinner" aria-label="Loading" />
            </div>
          ) : log.length === 0 ? (
            <p class="hint">Nothing forwarded yet.</p>
          ) : (
            <ul role="list">
              {log.map((f) => (
                <li key={f.message_id}>
                  <button class="row" onClick={() => onOpenMessage(f.message_id, f.folder)}>
                    <span class="row-top">
                      <span class="row-from">{sender(f.from_name, f.from_addr)}</span>
                      <span class="row-date">{listDate(f.date)}</span>
                    </span>
                    <span class="row-subject">
                      <span class="row-subject-text">{f.subject || "(no subject)"}</span>
                    </span>
                    {f.error && f.status !== "sent" && <span class="row-snippet warn-text">{f.error}</span>}
                    <span class="row-meta">
                      <StatusTag entry={f} />
                      <span class="tag">→ {f.to.join(", ")}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div class="section-head">
        <span>Matching mail already here</span>
        {previewing ? <span class="spinner small-spinner" aria-label="Loading" /> : preview && <span>{preview.total.toLocaleString()}</span>}
      </div>
      {!preview ? (
        !previewing && <p class="hint">Type a sender or a subject text to see which mail it matches.</p>
      ) : preview.total === 0 ? (
        <p class="hint">No mail in the archive matches.</p>
      ) : (
        <ul role="list">
          {preview.messages.map((m) => (
            <li key={m.id}>
              <button class={`row ${m.deleted_at ? "deleted" : ""}`} onClick={() => onOpenMessage(m.id, m.folders[0] ?? null)}>
                <span class="row-top">
                  <span class="row-from">{sender(m.from_name, m.from_addr)}</span>
                  <span class="row-date">{listDate(m.date)}</span>
                </span>
                <span class="row-subject">
                  <span class="row-subject-text">{m.subject || "(no subject)"}</span>
                </span>
                <span class="row-meta">
                  {m.deleted_at ? <span class="tag warn">Deleted</span> : m.folders.map((f) => <span class="tag" key={f}>{f}</span>)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {more > 0 && <p class="hint">…and {more.toLocaleString()} older.</p>}
    </div>
  );
}

function StatusTag({ entry }: { entry: ForwardLogEntry }) {
  switch (entry.status) {
    case "sent":
      return <span class="tag">Sent {relativeTime(entry.sent_at)}</span>;
    case "pending":
      return <span class="tag current">{entry.attempts ? `Trying again (attempt ${entry.attempts + 1})` : "Waiting"}</span>;
    case "failed":
      return <span class="tag warn">Failed after {entry.attempts} {entry.attempts === 1 ? "attempt" : "attempts"}</span>;
    default:
      return <span class="tag">Skipped: the rule was off</span>;
  }
}
